import Link from 'next/link'
import Author from '@/components/molecules/Author'
import NewsDate from '@/components/atoms/NewsDate'
import Time from '@/components/atoms/Time'
import styles from './ArticlePreview.module.scss'
import {
  formatDate,
  getHrReadableLink,
  getReadableLink,
  getReadingTime,
} from '@/helpers/utils'
import { Readable } from '../../../generated/apolloComponents'

interface Props {
  size: string
  article: Readable
  className?: string
  itLink?: boolean
}

const classes = {
  size: {
    big: {
      partWidth: 'w-full md:w-1/2',
      titleSize: 'text-4xl',
      containerClass: styles['articlePreview--big'],
    },
    small: {
      partWidth: 'lg:w-full sm:w-1/2',
      titleSize: 'text-lg leading-6 md:text-2xl',
      containerClass: styles['articlePreview--small'],
    },
  },
}

function ArticlePreview({
  size,
  article,
  className = '',
  itLink = true,
}: Props) {
  const { partWidth } = classes.size[size.toLowerCase() as 'big' | 'small']
  const { containerClass } = classes.size[size.toLowerCase() as 'big' | 'small']
  const { titleSize } = classes.size[size.toLowerCase() as 'big' | 'small']
  const readingTime = getReadingTime(Number(article?.wordCount))
  const articleDate = formatDate(
    article.updated && article.updated > article.publishedAt
      ? article.updated
      : article.publishedAt,
    'pl-PL'
  )
  const link = itLink ? getReadableLink(article) : getHrReadableLink(article)
  const target = article.readableType === 'youtube' ? '_blank' : undefined

  return (
    <article
      className={`${containerClass} ${
        className || ''
      } overflow-hidden shadow-md flex flex-wrap bg-white rounded-br-4xl`}
    >
      <div className={`${partWidth} ${styles.articlePreview__image}`}>
        {article?.cover?.url ? (
          <a
            href={link}
            title={article?.title as string}
            className={`block image-fit-container ${
              size === 'big' ? 'image-fit-container--big' : ''
            }`}
            target={target}
          >
            <img
              src={article.cover.url!}
              alt={article.title!}
              loading='lazy'
              width='1'
              height='1'
              className={`image-fit ${size === 'big' ? 'relative' : ''}`}
            />
          </a>
        ) : null}
      </div>
      <div
        className={`${partWidth} ${styles.articlePreview__content} pt-4  lg:pt-14 pb-4 lg:pb-8 pl-4 lg:pl-14 pr-4 lg:pr-10 flex flex-col`}
      >
        <h3 className={titleSize}>
          <Link
            href={link}
            prefetch={false}
            className='hover:text-primary transition-colors font-bold'
            title={article?.title as string}
            target={target}
          >
            {article?.title}
          </Link>
        </h3>
        <div className='mt-auto flex flex-wrap items-center'>
          <div className='mr-auto mt-1 flex'>
            <NewsDate date={articleDate} />
            {!target && <Time time={readingTime} className='ml-4 ' />}
          </div>
          <div className={styles.articlePreview__author}>
            <Author
              isPerson={!!article?.author?.photo?.url!}
              name={article?.author?.name! || article.author?.company!}
              position={article?.author?.position || undefined}
              company={article?.author?.company || undefined}
              image={
                article?.author?.photo?.url! ||
                article.author?.companyLogo?.url!
              }
            />
          </div>
        </div>
      </div>
    </article>
  )
}

export default ArticlePreview
